import { User } from '@kalos/kalos-rpc';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@kalos/ui';
import { useNavigate } from '@tanstack/react-router';
import { type UseFormReturn } from 'react-hook-form';

import { type FilterDimension } from '../../../../components/FilterSheet';
import { ListResultsPhone } from '../../../../components/ListPhone';
import { currentSearchHref } from '../../../../components/searchReturnTo';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { type UserFilters } from '../../../../hooks/react-query/useUserQuery';
import { UserClientService } from '../../../../tools/helpers';
import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';
import { type ListSort } from '../listState';
import { PhoneSearchLayout } from '../PhoneSearchLayout';
import { SearchResultRow } from '../SearchResultRow';
import { useSearchPages } from '../useSearchPages';
import CustomerSearchForm from './CustomerSearchForm';
import { defaultUserSearchValues, type UserSearchSchemeType } from './utils';

const dimensions: FilterDimension<UserSearchSchemeType>[] = [
  {
    id: 'firstname',
    label: 'First Name',
    fields: ['firstname'],
    summary: (v) => v.firstname ?? '',
  },
  { id: 'lastname', label: 'Last Name', fields: ['lastname'], summary: (v) => v.lastname ?? '' },
  {
    id: 'businessname',
    label: 'Business Name',
    fields: ['businessname'],
    summary: (v) => v.businessname ?? '',
  },
  { id: 'phone', label: 'Phone Number', fields: ['phone'], summary: (v) => v.phone ?? '' },
  { id: 'email', label: 'Email', fields: ['email'], summary: (v) => v.email ?? '' },
];

/** The table's sortable columns, each way. Last Name, A to Z, is the default. */
const sortOptions = [
  ['lastname', 'Last Name'],
  ['firstname', 'First Name'],
  ['businessname', 'Business Name'],
  ['phone', 'Primary Phone'],
  ['email', 'Email'],
  ['address', 'Address'],
].flatMap(([id, label]) => [
  { value: `${id}.asc`, label: `${label}, A to Z` },
  { value: `${id}.desc`, label: `${label}, Z to A` },
]);

const customerName = (user: User) =>
  [user.firstname, user.lastname]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

/**
 * Customers on phones: search by last name, the other fields under Filter, and one row per
 * customer that opens it. Edit, when the screen allows it, is the row's one other action.
 * (A search across names, email and phones waits on a kalos-core fix to SearchUsers.)
 */
export const CustomersSearchPhone = ({
  form,
  filterCriteria,
  sort,
  onSortChange,
  onDelete,
}: {
  form: UseFormReturn<UserSearchSchemeType>;
  /** The table's request without its page number. */
  filterCriteria: UserFilters;
  sort: ListSort | null;
  onSortChange: (sort: ListSort | null) => void;
  onDelete?: (user: User) => void;
}) => {
  const navigate = useNavigate();
  const { orderBy, orderDir, ...requestWithPage } = filterCriteria;
  // Each page sets its own number.
  const request = { ...requestWithPage, pageNumber: undefined };
  const pages = useSearchPages({
    queryKey: [
      queryKeys.user.root,
      queryKeys.user.list,
      'pages',
      queryKeys.user.getHash({ ...request, orderBy, orderDir }),
    ],
    fetchPage: (pageNumber) =>
      UserClientService.BatchGet(User.create({ ...request, orderBy, orderDir, pageNumber })),
  });

  return (
    <div className="flex flex-col gap-3 px-4 pb-4">
      <PhoneSearchLayout
        form={form}
        searchField="lastname"
        searchPlaceholder="Search by last name"
        defaultFilters={defaultUserSearchValues}
        dimensions={dimensions}
        sheetTitle="Filter customers"
        renderFields={() => <CustomerSearchForm inSheet />}
        sortOptions={sortOptions}
        sort={sort}
        defaultSort={{ id: 'lastname', desc: false }}
        onSortChange={onSortChange}
      >
        {({ isFiltered, clearFilters }) => (
          <ListResultsPhone
            itemLabel="customers"
            emptyTitle="No results."
            rowCount={pages.rows.length}
            totalCount={pages.totalCount}
            isPending={pages.isPending}
            isFetching={pages.isFetching}
            isError={pages.isError}
            isFetchingNextPage={pages.isFetchingNextPage}
            hasNextPage={pages.hasNextPage}
            onLoadMore={() => void pages.fetchNextPage()}
            onRetry={() => void pages.refetch()}
            isFiltered={isFiltered}
            onClearFilters={clearFilters}
          >
            {pages.rows.map((user) => {
              const name = customerName(user);
              const business = user.businessname?.trim();
              // Some records have no name at all; the email or phone still identifies them.
              const title =
                business ||
                name ||
                user.email?.trim() ||
                (user.phone ? formatPhoneNumber(user.phone) : '') ||
                'Unnamed customer';
              return (
                <SearchResultRow
                  key={user.id}
                  title={title}
                  lines={[
                    business ? name : '',
                    user.phone ? formatPhoneNumber(user.phone) : '',
                    [user.address, user.city]
                      .map((part) => part?.trim())
                      .filter(Boolean)
                      .join(', '),
                  ]}
                  actionLabel="View customer"
                  // Straight to the record, carrying this search so Back returns to it. Editing
                  // lives on the record, not on the row.
                  onSelect={() =>
                    navigate({
                      to: '/customer-details/$customerId',
                      params: { customerId: user.id },
                      search: { returnTo: currentSearchHref() },
                    })
                  }
                  corner={
                    onDelete ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`More actions for ${title}`}
                          >
                            More
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => onDelete(user)}>
                            Delete customer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : undefined
                  }
                />
              );
            })}
          </ListResultsPhone>
        )}
      </PhoneSearchLayout>
    </div>
  );
};
